import { useMemo, useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router";
import { CheckCircle2, ImageIcon, RefreshCw, XCircle } from "lucide-react";
import { FormattedMessage, useIntl } from "react-intl";

import { ImportedInstagramPostCard } from "@/components/campaign/social-post-preview-card";
import {
  CenteredLoader,
  EmptyCard,
  InlineNotice,
  MetricTile,
  PageIntro,
} from "@/components/common";
import { getUnavailableBrandPath, buildBrandPath, useRouteBrand } from "@/hooks/use-route-brand";
import { getInstagramCallbackErrorMessage } from "@/lib/instagram-callback-errors";
import { INSTAGRAM_INTEGRATION_CHANNEL } from "@/lib/integrations";
import { trpc } from "@/lib/trpc";
import { Badge, Button, Card, CardContent, CardHeader, CardTitle, cn } from "@runcheetah/ui";

type CallbackStatus = "success" | "error";

const isCallbackStatus = (value: string | null): value is CallbackStatus =>
  value === "success" || value === "error";

export default function InstagramIntegrationPage() {
  const intl = useIntl();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { brand, brandQuery } = useRouteBrand();
  const utils = trpc.useUtils();
  const [actionError, setActionError] = useState("");
  const [isRedirecting, setIsRedirecting] = useState(false);

  const instagramParam = searchParams.get("instagram");
  const callback = isCallbackStatus(instagramParam) ? instagramParam : null;
  const callbackErrorMessage = getInstagramCallbackErrorMessage(searchParams.get("reason"), intl);

  const importQuery = trpc.socialConnection.getInstagramImport.useQuery(
    { brandId: brand?.id ?? "", limit: 50 },
    { enabled: Boolean(brand?.id), staleTime: 0, refetchOnWindowFocus: true },
  );

  const disconnect = trpc.socialConnection.disconnectInstagram.useMutation({
    onSuccess: async () => {
      setActionError("");
      if (!brand?.id) return;
      await utils.socialConnection.getInstagramImport.invalidate({ brandId: brand.id });
      await utils.socialConnection.getInstagram.invalidate({ brandId: brand.id });
      navigate(buildBrandPath(brand.id, "/integrations"));
    },
    onError: (error) => setActionError(error.message),
  });

  const onConnect = async () => {
    if (!brand?.id) return;

    setActionError("");
    setIsRedirecting(true);
    try {
      const { url } = await utils.socialConnection.getInstagramAuthUrl.fetch({
        brandId: brand.id,
        returnTo: `${window.location.origin}${buildBrandPath(brand.id, "/integrations/instagram")}`,
      });
      window.location.assign(url);
    } catch (error) {
      setIsRedirecting(false);
      setActionError(
        error instanceof Error
          ? error.message
          : intl.formatMessage({
              id: "integrations.instagram.authStartFailed",
              defaultMessage: "Failed to start Instagram auth.",
            }),
      );
    }
  };

  const importData = importQuery.data;
  const connection = importData?.connection ?? null;
  const sync = importData?.sync ?? null;
  const media = useMemo(() => importData?.media ?? [], [importData?.media]);
  const status = connection?.status ?? null;
  const isConnected = status === "connected";
  const needsReconnect = status === "expired" || status === "revoked" || status === "error";
  const syncStatus = sync?.status ?? null;
  const accountLabel =
    connection?.accountName ??
    intl.formatMessage({
      id: "instagramIntegration.accountFallback",
      defaultMessage: "Instagram account",
    });

  if (brandQuery.isPending) {
    return (
      <CenteredLoader
        label={intl.formatMessage({
          id: "instagramIntegration.loadingBrand",
          defaultMessage: "Loading Instagram integration…",
        })}
      />
    );
  }

  if (!brand || brand.status !== "scanned") {
    return <Navigate to={getUnavailableBrandPath(brand?.id)} replace />;
  }

  return (
    <div className="mx-auto w-full max-w-7xl space-y-6 px-6 py-6">
      <PageIntro
        title={intl.formatMessage({
          id: "instagramIntegration.title",
          defaultMessage: "Instagram",
        })}
        actions={
          <InstagramIntegrationActions
            isConnected={isConnected}
            needsReconnect={needsReconnect}
            disconnecting={disconnect.isPending}
            isRedirecting={isRedirecting}
            onConnect={onConnect}
            onDisconnect={() => disconnect.mutate({ brandId: brand.id })}
          />
        }
      />

      {callback === "success" ? (
        <InlineNotice tone="success" icon={<CheckCircle2 className="h-4 w-4" />}>
          <FormattedMessage
            id="integrations.instagramConnectedSuccess"
            defaultMessage="Instagram connected successfully."
          />
        </InlineNotice>
      ) : null}

      {callback === "error" ? (
        <InlineNotice tone="danger" icon={<XCircle className="h-4 w-4" />}>
          {callbackErrorMessage}
        </InlineNotice>
      ) : null}

      {actionError ? (
        <InlineNotice tone="danger" icon={<XCircle className="h-4 w-4" />}>
          {actionError}
        </InlineNotice>
      ) : null}

      {importQuery.isPending ? (
        <CenteredLoader
          label={intl.formatMessage({
            id: "integrations.instagram.checkingConnection",
            defaultMessage: "Checking connection…",
          })}
        />
      ) : importQuery.error ? (
        <InlineNotice tone="danger" icon={<XCircle className="h-4 w-4" />}>
          {intl.formatMessage(
            {
              id: "instagramIntegration.loadFailed",
              defaultMessage: "Failed to load Instagram integration: {message}",
            },
            { message: importQuery.error.message },
          )}
        </InlineNotice>
      ) : !connection ? (
        <InstagramNotConnectedPanel
          isRedirecting={isRedirecting}
          onConnect={onConnect}
          description={intl.formatMessage(INSTAGRAM_INTEGRATION_CHANNEL.note)}
        />
      ) : (
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_22rem]">
          <main className="space-y-6">
            <section className="grid gap-4 sm:grid-cols-2">
              <MetricTile
                label={intl.formatMessage({
                  id: "instagramIntegration.metric.importedPosts",
                  defaultMessage: "Imported posts",
                })}
                value={intl.formatNumber(sync?.importedCount ?? media.length)}
              />
              <MetricTile
                label={intl.formatMessage({
                  id: "instagramIntegration.metric.recentPosts",
                  defaultMessage: "Recent posts",
                })}
                value={intl.formatNumber(media.length)}
              />
              {/* TODO: Restore Instagram insight metrics after Meta approves the permission. */}
            </section>

            <section className="space-y-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h2 className="text-xl font-semibold tracking-tight">
                    <FormattedMessage
                      id="instagramIntegration.importedPostsTitle"
                      defaultMessage="Imported posts"
                    />
                  </h2>
                  <p className="mt-1 text-sm text-muted-foreground">
                    <FormattedMessage
                      id="instagramIntegration.importedPostsDescription"
                      defaultMessage="Recent Instagram media imported into this brand workspace."
                    />
                  </p>
                </div>
                {syncStatus ? <InstagramSyncBadge status={syncStatus} /> : null}
              </div>

              {syncStatus === "queued" || syncStatus === "processing" ? (
                <InlineNotice icon={<RefreshCw className="h-4 w-4 animate-spin" />}>
                  <FormattedMessage
                    id="integrations.instagram.syncPendingHint"
                    defaultMessage="Recent posts will appear here after the worker finishes."
                  />
                </InlineNotice>
              ) : null}

              {sync?.lastError ? (
                <InlineNotice tone="danger" icon={<XCircle className="h-4 w-4" />}>
                  {sync.lastError}
                </InlineNotice>
              ) : null}

              {media.length === 0 ? (
                <EmptyCard icon={<ImageIcon className="h-8 w-8" />} contentClassName="h-56">
                  <FormattedMessage
                    id="integrations.instagram.noRecentMedia"
                    defaultMessage="No recent Instagram media was imported yet."
                  />
                </EmptyCard>
              ) : (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
                  {media.map((item) => (
                    <ImportedInstagramPostCard
                      key={item.id}
                      media={item}
                      profileLabel={accountLabel}
                    />
                  ))}
                </div>
              )}
            </section>
          </main>

          <aside className="space-y-4">
            <InstagramStatusPanel
              accountLabel={accountLabel}
              authFlow={connection.authFlow}
              status={status}
              expiresAt={connection.expiresAt}
              lastSyncedAt={sync?.lastSyncedAt ?? null}
              lastError={connection.lastError}
            />
          </aside>
        </div>
      )}
    </div>
  );
}

function InstagramIntegrationActions({
  isConnected,
  needsReconnect,
  disconnecting,
  isRedirecting,
  onConnect,
  onDisconnect,
}: {
  isConnected: boolean;
  needsReconnect: boolean;
  disconnecting: boolean;
  isRedirecting: boolean;
  onConnect: () => void;
  onDisconnect: () => void;
}) {
  const intl = useIntl();

  if (isConnected) {
    return (
      <Button variant="outline" onClick={onDisconnect} disabled={disconnecting}>
        {disconnecting
          ? intl.formatMessage({
              id: "integrations.instagram.disconnecting",
              defaultMessage: "Disconnecting…",
            })
          : intl.formatMessage({
              id: "integrations.instagram.disconnect",
              defaultMessage: "Disconnect",
            })}
      </Button>
    );
  }

  return (
    <div className="flex flex-wrap gap-2">
      <Button onClick={onConnect} disabled={isRedirecting}>
        {isRedirecting
          ? intl.formatMessage({
              id: "integrations.instagram.redirecting",
              defaultMessage: "Redirecting…",
            })
          : needsReconnect
            ? intl.formatMessage({
                id: "integrations.instagram.reconnectDirect",
                defaultMessage: "Reconnect with Instagram Login",
              })
            : intl.formatMessage({
                id: "integrations.instagram.connectDirect",
                defaultMessage: "Connect with Instagram Login",
              })}
      </Button>
      {needsReconnect ? (
        <Button variant="ghost" onClick={onDisconnect} disabled={disconnecting}>
          {disconnecting
            ? intl.formatMessage({
                id: "integrations.instagram.disconnecting",
                defaultMessage: "Disconnecting…",
              })
            : intl.formatMessage({
                id: "integrations.instagram.remove",
                defaultMessage: "Remove",
              })}
        </Button>
      ) : null}
    </div>
  );
}

function InstagramNotConnectedPanel({
  description,
  isRedirecting,
  onConnect,
}: {
  description: string;
  isRedirecting: boolean;
  onConnect: () => void;
}) {
  return (
    <Card className="border-border/70 bg-card/90 shadow-sm">
      <CardContent className="flex flex-col gap-5 p-6 md:flex-row md:items-center md:justify-between">
        <div className="max-w-xl space-y-2">
          <h2 className="text-lg font-semibold">
            <FormattedMessage
              id="instagramIntegration.notConnectedTitle"
              defaultMessage="Instagram is not connected"
            />
          </h2>
          <p className="text-sm leading-6 text-muted-foreground">{description}</p>
        </div>
        <InstagramIntegrationActions
          isConnected={false}
          needsReconnect={false}
          disconnecting={false}
          isRedirecting={isRedirecting}
          onConnect={onConnect}
          onDisconnect={() => undefined}
        />
      </CardContent>
    </Card>
  );
}

function InstagramStatusPanel({
  accountLabel,
  authFlow,
  status,
  expiresAt,
  lastSyncedAt,
  lastError,
}: {
  accountLabel: string;
  authFlow: string | null;
  status: string | null;
  expiresAt: Date | string | null;
  lastSyncedAt: Date | string | null;
  lastError: string | null;
}) {
  const intl = useIntl();

  return (
    <Card className="border-border/70 bg-card/90 shadow-sm">
      <CardHeader className="space-y-3">
        <div className="flex items-center justify-between gap-3">
          <CardTitle className="text-base">
            <FormattedMessage
              id="instagramIntegration.connectionTitle"
              defaultMessage="Connection"
            />
          </CardTitle>
          <InstagramConnectionStatusBadge status={status} />
        </div>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <InstagramDetailRow
          label={intl.formatMessage({
            id: "integrations.instagram.details.account",
            defaultMessage: "Account",
          })}
          value={accountLabel}
        />
        <InstagramDetailRow
          label={intl.formatMessage({
            id: "integrations.instagram.details.method",
            defaultMessage: "Method",
          })}
          value={intl.formatMessage(
            authFlow === "meta"
              ? {
                  id: "integrations.instagram.authFlow.meta",
                  defaultMessage: "Meta Business",
                }
              : {
                  id: "integrations.instagram.authFlow.direct",
                  defaultMessage: "Instagram Login",
                },
          )}
        />
        <InstagramDetailRow
          label={intl.formatMessage({
            id: "integrations.instagram.details.tokenExpires",
            defaultMessage: "Token expires",
          })}
          value={
            expiresAt
              ? intl.formatDate(new Date(expiresAt), { dateStyle: "medium" })
              : intl.formatMessage({
                  id: "integrations.instagram.never",
                  defaultMessage: "Never",
                })
          }
        />
        <InstagramDetailRow
          label={intl.formatMessage({
            id: "instagramIntegration.lastImport",
            defaultMessage: "Last import",
          })}
          value={
            lastSyncedAt
              ? intl.formatDate(new Date(lastSyncedAt), {
                  dateStyle: "medium",
                  timeStyle: "short",
                })
              : intl.formatMessage({
                  id: "integrations.instagram.waitingForFirstImport",
                  defaultMessage: "Waiting for first import",
                })
          }
        />
        {lastError ? <p className="text-sm text-destructive">{lastError}</p> : null}
      </CardContent>
    </Card>
  );
}

function InstagramDetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-4 rounded-xl border border-border/70 bg-muted/20 px-3 py-2">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="min-w-0 truncate text-right font-medium">{value}</span>
    </div>
  );
}

function InstagramConnectionStatusBadge({ status }: { status: string | null }) {
  const intl = useIntl();
  const variant =
    status === "connected"
      ? "default"
      : status === "expired" || status === "revoked" || status === "error"
        ? "destructive"
        : "outline";

  return (
    <Badge
      shape="pill"
      variant={variant}
      className={cn(variant === "outline" && "border-border/70 bg-muted/40 text-foreground")}
    >
      {status
        ? intl.formatMessage({
            id: `integrations.instagram.status.${status}`,
            defaultMessage:
              status === "connected"
                ? "Connected"
                : status === "expired"
                  ? "Expired"
                  : status === "revoked"
                    ? "Revoked"
                    : "Error",
          })
        : intl.formatMessage({
            id: "integrations.instagram.status.notConnected",
            defaultMessage: "Not connected",
          })}
    </Badge>
  );
}

function InstagramSyncBadge({ status }: { status: string }) {
  const intl = useIntl();
  const variant = status === "failed" ? "destructive" : "outline";

  return (
    <Badge
      shape="pill"
      variant={variant}
      className={cn(variant === "outline" && "border-border/70 bg-background/80")}
    >
      {intl.formatMessage({
        id: `integrations.instagram.sync.${status}`,
        defaultMessage:
          status === "queued"
            ? "Import queued"
            : status === "processing"
              ? "Importing posts"
              : status === "done"
                ? "Posts imported"
                : "Import failed",
      })}
    </Badge>
  );
}
